/**
 * A última milha: fazer a configuração chegar na TELA, não só no banco.
 *
 * Antes, GET /pdvInfo/:uuid era chamado uma única vez, quando o operador
 * escolhia o PDV; o resultado ficava no Zustand com persist (localStorage). Não
 * havia polling, websocket nem revalidação — mesmo com o OPS atualizado, a tela
 * só mudava se alguém recarregasse. "Descer automaticamente" não existia na ponta.
 *
 * Este hook fecha isso do jeito mais simples que funciona: consulta a rota de
 * tempos em tempos e compara o `configVersao` que o backend devolve. Se mudou,
 * recarrega a configuração no Zustand e avisa. Se não mudou, não mexe em nada.
 *
 * A consulta é leve e só roda com um PDV selecionado. Intervalo padrão: 15s.
 */
import { useEffect, useRef } from "react";
import { useCombinedStore } from "../store";
import { carregarConfigDoPdv } from "../services/pdvConfig.service";

const INTERVALO_PADRAO_MS = 15000;

export function useConfigDoPdv(intervaloMs: number = INTERVALO_PADRAO_MS) {
  const { subsidiaryDataZustand, setSubsidiaryDataZustand, setMessageZustand } =
    useCombinedStore();

  const pdvUuid = subsidiaryDataZustand?.selectedPdv?.itemUuid;
  const versaoAtual = subsidiaryDataZustand?.selectedPdv?.configVersao;

  // Guardados em ref para o efeito não reiniciar a cada render.
  const referencias = useRef({
    subsidiaryDataZustand,
    setSubsidiaryDataZustand,
    setMessageZustand,
    versaoAtual,
  });
  referencias.current = {
    subsidiaryDataZustand,
    setSubsidiaryDataZustand,
    setMessageZustand,
    versaoAtual,
  };

  useEffect(() => {
    if (!pdvUuid) return;

    let ativo = true;

    const conferir = async () => {
      try {
        const config = await carregarConfigDoPdv(pdvUuid);
        if (!ativo) return;

        const atual = referencias.current;
        if (!config.configVersao || config.configVersao === atual.versaoAtual) {
          // Nada mudou no desenho da tela; só atualiza o aviso de pendência.
          atual.setSubsidiaryDataZustand({
            ...atual.subsidiaryDataZustand,
            selectedPdv: {
              ...atual.subsidiaryDataZustand.selectedPdv,
              configPendente: config.configPendente,
            },
          });
          return;
        }

        atual.setSubsidiaryDataZustand({
          ...atual.subsidiaryDataZustand,
          selectedPdv: {
            ...atual.subsidiaryDataZustand.selectedPdv,
            homeInfo: config.homeInfo,
            saleOptions: config.saleOptions,
            printers: config.printers,
            configVersao: config.configVersao,
            configPendente: config.configPendente,
          },
        });
        atual.setMessageZustand(
          "A configuração deste PDV foi atualizada pela Central.",
          "success"
        );
      } catch {
        // Rede fora ou backend reiniciando: o PDV continua com o que já tem.
        // Nenhum erro na cara do operador por causa disso.
      }
    };

    const temporizador = setInterval(conferir, intervaloMs);
    return () => {
      ativo = false;
      clearInterval(temporizador);
    };
  }, [pdvUuid, intervaloMs]);
}
